import { useState } from "react";

// OutcomeScatterChart — outcome score (y, %) vs time spent (x, s) scatter with
// average crosshair lines + hover tooltip. Faithful SVG port of the design
// rp-scatter; styling is qbe-scatter* (in OutcomeGraphStrongWeakStyles, injected
// by the parent OutcomeGraphStrongWeak). All copy arrives via props.
const C_SCORE_LINE = "#73D7E6";
const C_TIME_LINE = "#114859";

interface ScatterPoint {
  code: string;
  text: string;
  score: number;
  time: number;
}
interface ScatterLabels {
  title: string;
  perf: string;
  scoreLine: string;
  timeLine: string;
  axisTime: string;
  axisScore: string;
  score: string;
  time: string;
}

const OutcomeScatterChart = ({
  points,
  avgScore,
  avgTime,
  maxTime,
  labels,
}: {
  points: ScatterPoint[];
  avgScore: number;
  avgTime: number;
  maxTime: number;
  labels: ScatterLabels;
}) => {
  const W = 980;
  const H = 460;
  const padL = 64;
  const padB = 64;
  const padT = 24;
  const padR = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const safeMax = maxTime > 0 ? maxTime : 30;
  const xOf = (t: number) => padL + Math.min(t / safeMax, 1) * plotW;
  const yOf = (s: number) => padT + plotH - (s / 100) * plotH;
  const yAvg = yOf(avgScore);
  const xAvg = xOf(avgTime);
  const [hover, setHover] = useState<number | null>(null);
  const xTicks = Array.from({ length: 7 }, (_, i) => (safeMax / 6) * i);
  return (
    <div className="qbe-scatter">
      <h3 className="qbe-scatter-title">{labels.title}</h3>
      <div className="qbe-scatter-legend">
        <span className="qbe-scatter-legend-item">
          <i style={{ background: "var(--accent-coral)" }}></i>
          {labels.perf}
        </span>
        <span className="qbe-scatter-legend-item">
          <i className="qbe-scatter-legend-line" style={{ background: C_SCORE_LINE }}></i>
          {labels.scoreLine}
        </span>
        <span className="qbe-scatter-legend-item">
          <i className="qbe-scatter-legend-line" style={{ background: C_TIME_LINE }}></i>
          {labels.timeLine}
        </span>
      </div>
      <div className="qbe-scatter-plot">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="qbe-scatter-svg"
        >
          {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((g) => {
            const y = yOf(g);
            return (
              <g key={"y" + g}>
                <line
                  x1={padL}
                  y1={y}
                  x2={W - padR}
                  y2={y}
                  stroke="var(--catchup-gray-100)"
                  strokeWidth="1"
                />
                <text
                  x={padL - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="var(--text-muted)"
                >
                  {g}%
                </text>
              </g>
            );
          })}
          {xTicks.map((t, i) => {
            const x = xOf(t);
            return (
              <text
                key={"x" + i}
                x={x}
                y={H - padB + 22}
                textAnchor="middle"
                fontSize="12"
                fill="var(--text-muted)"
              >
                {Math.round(t)}s
              </text>
            );
          })}
          <line
            x1={padL}
            y1={yAvg}
            x2={W - padR}
            y2={yAvg}
            stroke={C_SCORE_LINE}
            strokeWidth="2"
            strokeDasharray="7 6"
          />
          <line
            x1={xAvg}
            y1={padT}
            x2={xAvg}
            y2={padT + plotH}
            stroke={C_TIME_LINE}
            strokeWidth="2"
            strokeDasharray="7 6"
          />
          <line
            x1={padL}
            y1={padT}
            x2={padL}
            y2={padT + plotH}
            stroke="var(--catchup-gray-200)"
            strokeWidth="1.5"
          />
          <line
            x1={padL}
            y1={padT + plotH}
            x2={W - padR}
            y2={padT + plotH}
            stroke="var(--catchup-gray-200)"
            strokeWidth="1.5"
          />
          {points.map((p, i) => (
            <g
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={xOf(p.time)}
                cy={yOf(p.score)}
                r={hover === i ? 13 : 9}
                fill="var(--accent-coral)"
                stroke="#fff"
                strokeWidth="2.5"
                style={{
                  transition: "r var(--duration-base) var(--ease-standard)",
                }}
              />
            </g>
          ))}
          <text
            x={padL + plotW / 2}
            y={H - 12}
            textAnchor="middle"
            fontSize="13"
            fontWeight="600"
            fill="var(--text-secondary)"
          >
            {labels.axisTime}
          </text>
          <text
            x={18}
            y={padT + plotH / 2}
            textAnchor="middle"
            fontSize="13"
            fontWeight="600"
            fill="var(--text-secondary)"
            transform={`rotate(-90 18 ${padT + plotH / 2})`}
          >
            {labels.axisScore}
          </text>
        </svg>
        {hover !== null ? (
          <div className="qbe-scatter-tip">
            <b>{points[hover].code}</b>
            <span>{points[hover].text}</span>
            <span className="qbe-scatter-tipstat">
              {labels.score}: <b>{points[hover].score.toFixed(1)}%</b> ·{" "}
              {labels.time}: <b>{points[hover].time.toFixed(1)}s</b>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OutcomeScatterChart;
